import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import './AssetManagement.css';
import { formatFileSize, formatDate } from './mockData';
import AssetUploadModal from './AssetUploadModal';
import { Modal, Button } from '../../common';
import { assetApi, leadMagnetApi } from '../../../services/apis/configuredApis';
import LoadingSpinner from '../../common/LoadingSpinner';
import Alert from '../../common/Alert';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { getApiBaseUrl } from '../../../utils/env';

/**
 * Transform API response from snake_case to camelCase
 */
const transformAsset = (apiAsset) => {
  return {
    id: apiAsset.id,
    fileName: apiAsset.file_name,
    fileSize: apiAsset.file_size_bytes,
    fileType: apiAsset.mime_type,
    uploadDate: apiAsset.created_at || apiAsset.uploaded_at,
    storageKey: apiAsset.storage_key,
    uploadedBy: apiAsset.uploaded_by,
    // Keep original fields for API calls
    ...apiAsset,
  };
};

const AssetManagement = forwardRef(({
  leadMagnets = [],
  refreshTrigger = 0,
  showUploadButton = true,
  onAssetChange,
}, ref) => {
  const isCerebralAdmin = currentUserHasCerebralAdminRole();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedMagnets, setSelectedMagnets] = useState([]);
  const [previewAsset, setPreviewAsset] = useState(null);

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await assetApi.assetListGet({
        search: null,
        mimeType: null,
        uploadedBy: null,
        limit: 100,
        offset: 0,
        assetType: 'lead_magnet',
      });
      
      // Axios wraps the response, so the actual data is in response.data
      const responseData = response?.data || response;
      const assetsList = responseData?.assets || [];
      
      // Transform from snake_case to camelCase
      const transformed = assetsList.map(transformAsset);
      
      setAssets(transformed);
    } catch (err) {
      console.error('Error loading assets:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load assets';
      setError(errorMessage);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets, refreshTrigger]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openUploadModal: () => {
      if (isCerebralAdmin) {
        setShowUploadModal(true);
      }
    },
  }));

  // Compute which lead magnets use each asset
  const getUsedByMagnets = (assetId) => {
    return leadMagnets
      .filter(magnet => (magnet.assetId || magnet.asset_id) === assetId)
      .map(magnet => magnet.id);
  };

  const handleAssetSuccess = () => {
    loadAssets();
    if (onAssetChange) onAssetChange();
  };

  const handleOpenAssignModal = (asset) => {
    setSelectedAsset(asset);
    // Get current assignments from lead magnets
    const currentAssignments = getUsedByMagnets(asset.id);
    setSelectedMagnets(currentAssignments);
    setAssignModalOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedAsset) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Asset assignment is done by updating lead magnets with asset_id
      // Get current assignments
      const currentAssignments = getUsedByMagnets(selectedAsset.id);
      
      // Find magnets to add (in selectedMagnets but not in currentAssignments)
      const toAdd = selectedMagnets.filter(id => !currentAssignments.includes(id));
      
      // Find magnets to remove (in currentAssignments but not in selectedMagnets)
      const toRemove = currentAssignments.filter(id => !selectedMagnets.includes(id));
      
      // Update lead magnets
      const updatePromises = [];
      
      // Add asset to selected magnets
      for (const magnetId of toAdd) {
        const magnet = leadMagnets.find(m => m.id === magnetId);
        if (magnet) {
          updatePromises.push(
            leadMagnetApi.leadMagnetUpdateLeadMagnetIdPut({
              leadMagnetId: magnetId,
              leadMagnetUpdate: {
                asset_id: selectedAsset.id,
              },
            })
          );
        }
      }
      
      // Remove asset from unselected magnets
      for (const magnetId of toRemove) {
        updatePromises.push(
          leadMagnetApi.leadMagnetUpdateLeadMagnetIdPut({
            leadMagnetId: magnetId,
            leadMagnetUpdate: {
              asset_id: null,
            },
          })
        );
      }
      
      await Promise.all(updatePromises);
      
      setAssignModalOpen(false);
      setSelectedAsset(null);
      setSelectedMagnets([]);
      loadAssets();
    } catch (err) {
      console.error('Error assigning asset:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to assign asset';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleMagnetSelection = (magnetId) => {
    setSelectedMagnets(prev => {
      if (prev.includes(magnetId)) {
        return prev.filter(id => id !== magnetId);
      } else {
        return [...prev, magnetId];
      }
    });
  };

  const getDownloadUrl = (assetId) => {
    const apiBase = getApiBaseUrl();
    return `${apiBase}/assets/${assetId}/download`;
  };

  const handleViewAsset = (asset) => {
    setPreviewAsset(asset);
  };

  const getMagnetName = (magnetId) => {
    const magnet = leadMagnets.find(m => m.id === magnetId);
    return magnet ? (magnet.internalName || magnet.internal_name) : `Magnet ${magnetId}`;
  };

  if (loading && assets.length === 0) {
    return (
      <div className="asset-management">
        <LoadingSpinner text="Loading assets..." />
      </div>
    );
  }

  return (
    <div className="asset-management">
      {error && (
        <Alert
          variant="error"
          onClose={() => setError(null)}
          className="asset-management__alert"
        >
          {error}
        </Alert>
      )}
      
      {showUploadButton && isCerebralAdmin && (
        <div className="asset-management__header-actions" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            onClick={() => setShowUploadModal(true)}
          >
            + Upload Asset
          </Button>
        </div>
      )}
      
      <div className="asset-management__table-container">
        {assets.length === 0 ? (
          <div className="asset-management__empty">
            <p>No assets uploaded yet. Upload your first asset to get started.</p>
          </div>
        ) : (
          <table className="asset-management__table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Size</th>
                <th>Upload Date</th>
                <th>Used By Magnets</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <div className="asset-management__file-name">
                      <strong>{asset.fileName}</strong>
                      <span className="asset-management__file-type">
                        {asset.fileType}
                      </span>
                    </div>
                  </td>
                  <td>{formatFileSize(asset.fileSize)}</td>
                  <td>{formatDate(asset.uploadDate)}</td>
                  <td>
                    {(() => {
                      const usedBy = getUsedByMagnets(asset.id);
                      return usedBy.length > 0 ? (
                        <div className="asset-management__magnets-list">
                          {usedBy.map((magnetId) => (
                            <span key={magnetId} className="asset-management__magnet-tag">
                              {getMagnetName(magnetId)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="asset-management__no-magnets">Not assigned</span>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="asset-management__actions">
                      <button
                        className="asset-management__btn asset-management__btn--view"
                        onClick={() => handleViewAsset(asset)}
                      >
                        View
                      </button>
                      {isCerebralAdmin && (
                        <button
                          className="asset-management__btn asset-management__btn--assign"
                          onClick={() => handleOpenAssignModal(asset)}
                        >
                          Assign
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal - Only show if user has CerebralAdmin role */}
      {isCerebralAdmin && (
        <AssetUploadModal
          show={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleAssetSuccess}
        />
      )}

      {/* Assign Modal - Only show if user has CerebralAdmin role */}
      {isCerebralAdmin && assignModalOpen && selectedAsset && (
        <Modal
          isOpen={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedAsset(null);
            setSelectedMagnets([]);
          }}
          title="Assign Asset to Lead Magnets"
          size="large"
          footer={
            <div className="">
              <Button
                variant="secondary"
                onClick={() => {
                  setAssignModalOpen(false);
                  setSelectedAsset(null);
                  setSelectedMagnets([]);
                }}
                type="button"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignSubmit}
                type="button"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Assignment'}
              </Button>
            </div>
          }
        >
          <div className="asset-management__assign-content">
            <p className="asset-management__asset-name">
              <strong>Asset:</strong> {selectedAsset.fileName}
            </p>
            <div className="asset-management__field">
              <label>Select Lead Magnets</label>
              {leadMagnets.length === 0 ? (
                <p className="asset-management__no-magnets-text">
                  No lead magnets available. Create a lead magnet first.
                </p>
              ) : (
                <div className="asset-management__magnets-checkbox-list">
                  {leadMagnets.map((magnet) => (
                    <label
                      key={magnet.id}
                      className="asset-management__checkbox-label"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMagnets.includes(magnet.id)}
                        onChange={() => toggleMagnetSelection(magnet.id)}
                      />
                      <span>{magnet.internalName}</span>
                      {magnet.status && (
                        <span className={`asset-management__status-badge asset-management__status-badge--${magnet.status}`}>
                          {magnet.status}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Asset Preview Modal */}
      {previewAsset && (
        <Modal
          isOpen={!!previewAsset}
          onClose={() => setPreviewAsset(null)}
          title="Asset Preview"
          size="large"
          footer={
            <div className="">
              <Button
                variant="secondary"
                onClick={() => setPreviewAsset(null)}
                type="button"
              >
                Close
              </Button>
              <a
                href={getDownloadUrl(previewAsset.id)}
                download={previewAsset.fileName}
                className="asset-management__btn asset-management__btn--primary"
                style={{ textDecoration: 'none', padding: '8px 16px' }}
              >
                Download
              </a>
            </div>
          }
        >
          <div className="asset-management__preview-content">
            <div className="asset-management__preview-meta">
              <div className="asset-management__preview-meta-row">
                <span className="asset-management__preview-label">File Name</span>
                <span className="asset-management__preview-value">{previewAsset.fileName}</span>
              </div>
              <div className="asset-management__preview-meta-row">
                <span className="asset-management__preview-label">Type</span>
                <span className="asset-management__preview-value">{previewAsset.fileType}</span>
              </div>
              <div className="asset-management__preview-meta-row">
                <span className="asset-management__preview-label">Size</span>
                <span className="asset-management__preview-value">{formatFileSize(previewAsset.fileSize)}</span>
              </div>
              <div className="asset-management__preview-meta-row">
                <span className="asset-management__preview-label">Uploaded</span>
                <span className="asset-management__preview-value">{formatDate(previewAsset.uploadDate)}</span>
              </div>
            </div>

            {previewAsset.fileType && previewAsset.fileType.startsWith('image/') && (
              <div className="asset-management__preview-image-container">
                <img
                  src={getDownloadUrl(previewAsset.id)}
                  alt={previewAsset.fileName}
                  className="asset-management__preview-image"
                />
              </div>
            )}

            {previewAsset.fileType === 'application/pdf' && (
              <div className="asset-management__preview-pdf-container">
                <iframe
                  src={getDownloadUrl(previewAsset.id)}
                  title={previewAsset.fileName}
                  className="asset-management__preview-pdf"
                />
              </div>
            )}

            {previewAsset.fileType && !previewAsset.fileType.startsWith('image/') && previewAsset.fileType !== 'application/pdf' && (
              <div className="asset-management__preview-nopreview">
                <p>Preview not available for this file type.</p>
                <p>Use the Download button to view the file.</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
});

AssetManagement.displayName = 'AssetManagement';

export default AssetManagement;

